import { useEffect, useMemo, useRef, useState } from "react";

import {
  applyFleetOrderOperationalCorrection,
  getFleetOrderCorrectionWorkspace,
} from "../../api/client.js";
import { Dialog } from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";

const REASONS = [
  ["DELIVERY_FAILED", "Pengiriman gagal"],
  ["CUSTOMER_REJECTED", "Pelanggan menolak"],
  ["DELIVERY_DEFERRED", "Pengiriman ditunda"],
  ["WRONG_ASSIGNMENT", "Salah penugasan"],
  ["DESTINATION_UNREACHABLE", "Tujuan tidak dapat dijangkau"],
  ["OPERATIONAL_MISTAKE", "Kesalahan operasional"],
  ["OTHER", "Lainnya"],
];

function requestKey(prefix) {
  if (globalThis.crypto?.randomUUID) return `${prefix}-${globalThis.crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function coordinateText(coordinate) {
  if (!coordinate) return "Belum tersedia";
  return `${Number(coordinate.latitude).toFixed(6)}, ${Number(coordinate.longitude).toFixed(6)}`;
}

function sourceLabel(source) {
  return {
    ACCURATE: "Accurate",
    CUSTOMER_DEFAULT: "Master pelanggan",
    CUSTOMER_DEFAULT_FALLBACK: "Master pelanggan",
    CUSTOMER_ACCURATE_FALLBACK: "Accurate pelanggan",
    SHIPPING_ADDRESS_MASTER: "Alamat kirim",
    SHIPPING_ADDRESS_SNAPSHOT: "Snapshot alamat kirim",
  }[String(source || "").toUpperCase()] || source || "Belum tersedia";
}

function fleetOrderContext(workspace, fleetOrder) {
  const status = String(workspace?.fleetOrder?.status || fleetOrder?.status || "").toUpperCase();
  const phase = String(workspace?.execution?.phase || "").toUpperCase();

  if (status === "COMPLETED" || phase === "CLOSED") {
    return {
      label: "Selesai",
      tone: "completed",
      consequence:
        "Dokumen akan dilepas dari Fleet Order yang telah selesai dan dapat dijadwalkan kembali. Riwayat perjalanan, bukti, dan Delivery Order tetap dipertahankan.",
    };
  }

  if (phase === "IN_PROGRESS" || phase === "ON_ROUTE" || phase === "RUNNING") {
    return {
      label: "Sedang berjalan",
      tone: "running",
      consequence:
        "Dokumen akan dikeluarkan dari sisa perjalanan dan dapat dijadwalkan kembali. Riwayat perjalanan, bukti, dan Delivery Order tidak dihapus.",
    };
  }

  return {
    label: "Terkunci",
    tone: "locked",
    consequence:
      "Dokumen akan dilepas melalui koreksi operasional dan dapat dijadwalkan kembali. Riwayat perjalanan, bukti, dan Delivery Order tidak dihapus.",
  };
}

function executionTone(status) {
  const normalized = String(status || "").toUpperCase();

  if (["FAILED", "CANCELLED"].includes(normalized)) return "danger";
  if (normalized === "DEFERRED") return "warning";
  if (["COMPLETED", "COMPLETED_WITH_ISSUE"].includes(normalized)) return "success";
  if (normalized === "EN_ROUTE") return "info";
  return "neutral";
}

function correctionTypeLabel(type) {
  return String(type || "").toUpperCase() === "DETACH_DOCUMENT"
    ? "Pengiriman dikeluarkan"
    : "Koordinat tujuan diperbarui";
}

function formatCorrectionTime(value) {
  if (!value) return "Waktu tidak tersedia";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

export function FleetOrderOperationalCorrectionDialog({
  fleetOrder,
  initialDocument = null,
  onApplied,
  onClose,
}) {
  const [workspace, setWorkspace] = useState(null);
  const [mode, setMode] = useState(initialDocument ? "detach" : "coordinate");
  const [selectedDocumentKey, setSelectedDocumentKey] = useState("");
  const [selectedStopKey, setSelectedStopKey] = useState("");
  const [reasonCode, setReasonCode] = useState(
    initialDocument ? "DELIVERY_FAILED" : "MASTER_COORDINATE_REFRESH",
  );
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const correlationKeys = useRef(new Map());

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getFleetOrderCorrectionWorkspace(fleetOrder.id)
      .then((payload) => {
        if (!active) return;
        setWorkspace(payload);
        const requested = initialDocument
          ? payload.documents?.find((document) =>
              document.documentType === String(initialDocument.documentType || "SALES_ORDER").toUpperCase()
              && Number(document.documentId) === Number(initialDocument.documentId || initialDocument.id))
          : null;
        const firstEligible = payload.documents?.find((document) => document.eligible);
        const selected = requested || firstEligible || payload.documents?.[0];
        if (selected) setSelectedDocumentKey(`${selected.documentType}:${selected.documentId}`);
        const firstCoordinate = payload.coordinateStops?.find((stop) => stop.canRecordRefresh)
          || payload.coordinateStops?.[0];
        if (firstCoordinate) setSelectedStopKey(firstCoordinate.stopKey);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fleetOrder.id, initialDocument]);

  const selectedDocument = useMemo(
    () => workspace?.documents?.find(
      (document) => `${document.documentType}:${document.documentId}` === selectedDocumentKey,
    ) || null,
    [selectedDocumentKey, workspace],
  );
  const selectedStop = useMemo(
    () => workspace?.coordinateStops?.find((stop) => stop.stopKey === selectedStopKey) || null,
    [selectedStopKey, workspace],
  );
  const context = useMemo(
    () => fleetOrderContext(workspace, fleetOrder),
    [fleetOrder, workspace],
  );

  const ready = reason.trim().length >= 20
    && confirmed
    && (mode === "coordinate"
      ? Boolean(selectedStop?.canRecordRefresh)
      : Boolean(selectedDocument?.eligible));

  async function submit() {
    if (!ready || busy) return;
    setBusy(true);
    setError("");
    try {
      const requestIdentity = mode === "coordinate"
        ? `coordinate:${selectedStop.stopKey}`
        : `detach:${selectedDocument.documentType}:${selectedDocument.documentId}`;
      if (!correlationKeys.current.has(requestIdentity)) {
        correlationKeys.current.set(
          requestIdentity,
          requestKey(`fo-correction-${fleetOrder.id}`),
        );
      }
      const payload = {
        actionType: mode === "coordinate"
          ? "REFRESH_DESTINATION_COORDINATE"
          : "DETACH_DOCUMENT",
        reason: reason.trim(),
        reasonCode,
        confirmCorrection: true,
        correlationKey: correlationKeys.current.get(requestIdentity),
      };
      if (mode === "coordinate") {
        payload.stopKey = selectedStop.stopKey;
      } else {
        payload.documentType = selectedDocument.documentType;
        payload.documentId = selectedDocument.documentId;
        payload.confirmRelease = true;
      }
      const response = await applyFleetOrderOperationalCorrection(fleetOrder.id, payload);
      onApplied(response);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      className="dialog-card--fleet-operational-correction"
      onClose={() => !busy && onClose()}
      title="Koreksi Operasional"
    >
      <div className="dialog-card__body fleet-operational-correction">
        <div className="fleet-operational-correction__intro">
          <Icon name="warning" size={18} />
          <div className="fleet-operational-correction__intro-copy">
            <div className="fleet-operational-correction__intro-heading">
              <strong>{fleetOrder.foNumber}</strong>
              <span className={`fleet-operational-correction__fo-status is-${context.tone}`}>
                {context.label}
              </span>
            </div>
            <span>
              Perubahan biasa tetap terkunci. Gunakan koreksi hanya untuk kebutuhan
              operasional yang dapat diaudit.
            </span>
          </div>
        </div>

        <div className="fleet-operational-correction__tabs" role="tablist" aria-label="Jenis koreksi">
          <button
            aria-selected={mode === "coordinate"}
            className={mode === "coordinate" ? "is-active" : ""}
            onClick={() => {
              setMode("coordinate");
              setReasonCode("MASTER_COORDINATE_REFRESH");
              setConfirmed(false);
              setError("");
            }}
            role="tab"
            type="button"
          >
            Perbaiki koordinat tujuan
          </button>
          <button
            aria-selected={mode === "detach"}
            className={mode === "detach" ? "is-active" : ""}
            onClick={() => {
              setMode("detach");
              setReasonCode("DELIVERY_FAILED");
              setConfirmed(false);
              setError("");
            }}
            role="tab"
            type="button"
          >
            Keluarkan pengiriman dari FO
          </button>
        </div>

        {loading && <div className="loading-state">Memuat data koreksi...</div>}
        {error && <div className="error-banner" role="alert">{error}</div>}

        {!loading && workspace && mode === "coordinate" && (
          <section className="fleet-operational-correction__section">
            <div className="fleet-operational-correction__section-heading">
              <span>Tujuan pengiriman</span>
              <strong>Gunakan koordinat master terbaru</strong>
            </div>

            <label>
              <span>Titik pengiriman</span>
              <select value={selectedStopKey} onChange={(event) => setSelectedStopKey(event.target.value)}>
                {workspace.coordinateStops.map((stop) => (
                  <option key={stop.stopKey} value={stop.stopKey}>
                    {stop.sequence}. {stop.customerName} · {stop.canRecordRefresh ? "Koordinat tersedia" : "Koordinat belum tersedia"}
                  </option>
                ))}
              </select>
            </label>

            {selectedStop && (
              <div className="fleet-operational-correction__coordinate-grid">
                <div>
                  <span>Koordinat master saat ini</span>
                  <strong>{coordinateText(selectedStop.currentCoordinate)}</strong>
                  <small>{sourceLabel(selectedStop.currentCoordinate?.source)}</small>
                </div>
                <div>
                  <span>Koordinat riwayat eksekusi</span>
                  <strong>{coordinateText(selectedStop.historicalExecutionCoordinate)}</strong>
                  <small>
                    {selectedStop.historicalCoordinateDiffers
                      ? "Berbeda dari master saat ini"
                      : "Riwayat tetap dipertahankan"}
                  </small>
                </div>
              </div>
            )}

            {!selectedStop?.canRecordRefresh && (
              <div className="warning-banner">
                Koordinat master masih belum valid. Perbaiki Data Master terlebih dahulu;
                foto dan riwayat lama tidak akan diubah.
              </div>
            )}
          </section>
        )}

        {!loading && workspace && mode === "detach" && (
          <section className="fleet-operational-correction__section">
            <div className="fleet-operational-correction__section-heading">
              <span>Pengiriman</span>
              <strong>Pilih dokumen yang perlu dilepas dari Fleet Order</strong>
            </div>

            <label>
              <span>Dokumen pengiriman</span>
              <select value={selectedDocumentKey} onChange={(event) => setSelectedDocumentKey(event.target.value)}>
                {workspace.documents.map((document) => (
                  <option key={`${document.documentType}:${document.documentId}`} value={`${document.documentType}:${document.documentId}`}>
                    {document.documentNumber} · {document.customerName} · {document.executionStatusLabel}
                  </option>
                ))}
              </select>
            </label>

            {selectedDocument && (
              <div className="fleet-operational-correction__document-state">
                <div>
                  <span>Status pengiriman</span>
                  <strong className={`fleet-operational-correction__delivery-status is-${executionTone(selectedDocument.executionStatus)}`}>
                    {selectedDocument.executionStatusLabel}
                  </strong>
                </div>
                <div className={`fleet-operational-correction__eligibility ${selectedDocument.eligible ? "is-eligible" : "is-blocked"}`}>
                  <strong>
                    {selectedDocument.eligible
                      ? "Dapat dikeluarkan dari FO"
                      : "Tidak dapat dikeluarkan"}
                  </strong>
                  <span>
                    {selectedDocument.eligible
                      ? "Dokumen memenuhi syarat Koreksi Operasional."
                      : selectedDocument.eligibilityReason}
                  </span>
                </div>
              </div>
            )}

            <label>
              <span>Kategori alasan</span>
              <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}>
                {REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </section>
        )}

        {!loading && workspace && (
          <section className="fleet-operational-correction__reason-card">
            <div className="fleet-operational-correction__section-heading">
              <span>Konfirmasi koreksi</span>
              <strong>Catat alasan operasional secara jelas</strong>
            </div>

            <label className="fleet-operational-correction__reason">
              <span>Alasan koreksi</span>
              <textarea
                maxLength={500}
                minLength={20}
                placeholder="Jelaskan kondisi operasional dan alasan koreksi (minimal 20 karakter)."
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <small>{reason.trim().length}/500 · minimal 20 karakter</small>
            </label>

            <label className="fleet-operational-correction__confirmation">
              <input
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                type="checkbox"
              />
              <span>
                <strong>Saya memahami akibat koreksi ini.</strong>
                <small>
                  {mode === "detach"
                    ? context.consequence
                    : "Koordinat master terbaru akan digunakan sebagai referensi operasional. Riwayat eksekusi tetap dipertahankan dan jalur tersisa dapat perlu dihitung ulang."}
                </small>
              </span>
            </label>
          </section>
        )}

        {!loading && workspace?.corrections?.length > 0 && (
          <details className="fleet-operational-correction__history">
            <summary>
              <span>Riwayat Koreksi Operasional</span>
              <strong>{workspace.corrections.length}</strong>
            </summary>
            <div className="fleet-operational-correction__history-list">
              {workspace.corrections.map((correction) => (
                <article key={correction.id}>
                  <div>
                    <strong>{correctionTypeLabel(correction.correctionType)}</strong>
                    <span>{formatCorrectionTime(correction.createdAt)}</span>
                  </div>
                  {correction.targetDocumentNumber && (
                    <span>{correction.targetDocumentNumber}</span>
                  )}
                  <p>{correction.reason}</p>
                  <small>
                    {correction.actorName
                      ? `Dicatat oleh ${correction.actorName}`
                      : "Pelaksana dicatat oleh sistem"}
                  </small>
                </article>
              ))}
            </div>
          </details>
        )}
      </div>

      <footer className="dialog-card__actions fleet-operational-correction__actions">
        <button className="secondary-button" disabled={busy} onClick={onClose} type="button">
          Batal
        </button>
        <button
          className={`primary-button fleet-operational-correction__submit ${
            mode === "detach" ? "is-danger" : "is-coordinate"
          }`}
          disabled={!ready || busy}
          onClick={submit}
          type="button"
        >
          {busy ? "Menerapkan..." : mode === "coordinate" ? "Terapkan Koordinat" : "Keluarkan Pengiriman"}
        </button>
      </footer>
    </Dialog>
  );
}
